import { useEffect, useState } from "react";
import axios from "axios";
import { debounce } from "lodash";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { Loader2 } from "lucide-react"; // spinner icon
import UserResponseCell from "./UserResponseCell";

export default function AppointmentsSection({
  appointments,
  quickConsultations,
  callbackRequests,
  fetchAppointments,
}) {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  // rename state
  const [quickConsultationsData, setQuickConsultationsData] = useState([]);

  // 🔹 Filters
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterDate, setFilterDate] = useState("");
  const [quickDate, setQuickDate] = useState("");
  const [callbackDate, setCallbackDate] = useState("");

  const [searchBookingId, setSearchBookingId] = useState("");
  const [activeTab, setActiveTab] = useState("appointments");

  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState("");

  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleError, setRescheduleError] = useState("");

  const buttonColorMap = {
    Canceled: "bg-red-600 hover:bg-red-700 text-white",
    Rescheduled: "bg-yellow-500 hover:bg-yellow-600 text-white",
    Confirmed: "bg-green-600 hover:bg-green-700 text-white",
  };

  // update user response
  const updateUserResponse = async (id, response) => {
    const ref = doc(db, "callbackRequests", id);
    await updateDoc(ref, { userResponse: response });
  };

  // 🔹 Update status
  const handleUpdateStatus = async (appt, status) => {
    try {
      setLoadingId(appt.id);

      if (!appt || !appt.id) {
        console.warn("Invalid appointment, cannot update status");
        return;
      }

      // 1️⃣ Update Firestore status
      const appointmentRef = doc(db, "appointments", appt.id);
      if (status === "Canceled") {
        if (!cancelReason.trim()) {
          setCancelError("Reason is required");
          return;
        }
        await updateDoc(appointmentRef, {
          status,
          cancelReason: cancelReason.trim(),
        });
      } else if (status === "Rescheduled") {
        if (!rescheduleDate || !rescheduleTime) {
          setRescheduleError("Date and Time are required");
          return;
        }
        await updateDoc(appointmentRef, {
          status,
          oldDate: appt.date,
          oldTime: appt.time,
          date: rescheduleDate,
          time: rescheduleTime,
        });
      } else {
        await updateDoc(appointmentRef, { status });
      }

      // 2️⃣ Prepare backend payload
      let payload = {};
      let endpoint = "";

      if (status === "Confirmed") {
        endpoint = "/api/appointment/confirm";
        payload = {
          email: appt.email || "",
          name: appt.name || "",
          bookingId: appt.bookingId || "",
          phone: appt.phone || "",
          date: appt.date || "",
          time: appt.time || "",
          service: appt.service || "",
        };
      } else if (status === "Canceled") {
        endpoint = "/api/appointment/cancel";
        payload = {
          email: appt.email || "",
          name: appt.name || "",
          bookingId: appt.bookingId || "",
          date: appt.date || "",
          time: appt.time || "",
          service: appt.service || "",
          reason: appt.cancelReason || "Not specified",
        };
      } else if (status === "Rescheduled") {
        endpoint = "/api/appointment/reschedule";
        payload = {
          email: appt.email || "",
          name: appt.name || "",
          bookingId: appt.bookingId || "",
          phone: appt.phone || "",
          old_date: appt.oldDate || appt.date || "",
          old_time: appt.oldTime || appt.time || "",
          new_date: rescheduleDate  || "",
          new_time: rescheduleTime  || "",
          service: appt.service || "",
        };
      } else {
        console.warn("Unknown status:", status);
        return;
      }

      // 3️⃣ Send JSON request to backend
      await axios.post(
        `https://vpjc-api-805872224468.asia-south1.run.app${endpoint}`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      console.log(
        `Status "${status}" updated and email sent for ${payload.name}`
      );

      // 4️⃣ Refresh appointments
      fetchAppointments();
      setCancelReason("");
      setCancelError("");
      setRescheduleDate(null);
      setRescheduleTime("");
      setRescheduleError("");
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setLoadingId(null);
    }
  };

  const statusLabelMap = {
    Confirmed: "Confirm",
    Canceled: "Cancel",
    Rescheduled: "Reschedule",
  };

  const statusSentenceMap = {
    Confirmed: "confirm",
    Canceled: "cancel",
    Rescheduled: "reschedule",
  };

  // keep local state in sync when parent sends data
  useEffect(() => {
    if (quickConsultations) {
      setQuickConsultationsData(quickConsultations);
    }
  }, [quickConsultations]);
  const handleQuickStatus = async (consultation, action) => {
    try {
      setLoadingId(consultation.id);

      const newStatus = action === "Answered" ? "Answered" : "Follow-up Needed";
      const ref = doc(db, "quickConsultations", consultation.id);

      await updateDoc(ref, { status: newStatus });

      // ✅ instant UI update
      setQuickConsultationsData((prev) =>
        prev.map((q) =>
          q.id === consultation.id ? { ...q, status: newStatus } : q
        )
      );

      console.log(`Quick consultation ${consultation.id} → ${newStatus}`);
    } catch (err) {
      console.error("Error updating quick consultation status:", err);
    } finally {
      setLoadingId(null);
    }
  };

  const debouncedUpdateQuickResponse = debounce(async (id, response) => {
    try {
      const ref = doc(db, "quickConsultations", id);
      await updateDoc(ref, { userResponse: response });
      console.log(`Quick consultation ${id} → response: ${response}`);
    } catch (err) {
      console.error("Error updating quick consultation response:", err);
    }
  }, 500);

  const handleCallbackStatus = async (callback, action) => {
    try {
      setLoadingId(callback.id);

      const newStatus = action === "Answered" ? "Answered" : "Follow-up Needed";
      const ref = doc(db, "callbackRequests", callback.id);

      await updateDoc(ref, { status: newStatus });

      // ✅ Always fetch fresh data
      await fetchAppointments();

      console.log(`Updated callback ${callback.id} → ${newStatus}`);
    } catch (err) {
      console.error("Error updating status:", err);
    } finally {
      setLoadingId(null);
    }
  };

  // 🔹 Filter appointments
  const filteredAppointments = (appointments || []).filter((a) => {
    const typeMatch = filterType === "All" || a.consultationType === filterType;
    const statusMatch = filterStatus === "All" || a.status === filterStatus;

    const apptDate = new Date(a.date);
    const formattedDate = isNaN(apptDate.getTime())
      ? ""
      : apptDate.toISOString().split("T")[0];
    const dateMatch = filterDate === "" || formattedDate === filterDate;

    const bookingIdMatch =
      searchBookingId === "" ||
      (a.bookingId || a.id || "")
        .toLowerCase()
        .includes(searchBookingId.toLowerCase());

    return typeMatch && statusMatch && dateMatch && bookingIdMatch;
  });

  // Convert Firestore Timestamp or JS Date into YYYY-MM-DD string
  const formatDate = (val) => {
    if (!val) return "";
    const d = val?.toDate ? val.toDate() : new Date(val);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0]; // YYYY-MM-DD
  };

  const filteredQuickConsultations = (quickConsultationsData || []).filter(
    (q) => {
      const formattedDate = formatDate(q.createdAt);
      return quickDate === "" || formattedDate === quickDate;
    }
  );

  const filteredCallbackRequests = (callbackRequests || []).filter((c) => {
    const formattedDate = formatDate(c.createdAt);
    return callbackDate === "" || formattedDate === callbackDate;
  });

  // 🔹 Columns to show
  const getColumns = () => {
    switch (filterType) {
      case "Walk-in":
        return [
          "Booking ID",
          "Booked On",
          "Appointment Date",
          "Time",
          "Name",
          "Email",
          "Phone",
          "Consultation Type",
          "Service",
          "Branch",
          "Status",
          "Actions",
        ];

      case "Home Visit":
        return [
          "Booking ID",
          "Booked On",
          "Appointment Date",
          "Time",
          "Name",
          "Email",
          "Phone",
          "Consultation Type",
          "Service",
          "Pincode",
          "Locality",
          "Flat No",
          "Landmark",
          "Status",
          "Actions",
        ];

      case "Virtual Consultation":
        return [
          "Booking ID",
          "Booked On",
          "Appointment Date",
          "Time",
          "Name",
          "Email",
          "Phone",
          "Consultation Type",
          "Service",
          "Status",
          "Actions",
        ];

      case "All":
        return [
          "Booking ID",
          "Booked On",
          "Appointment Date",
          "Time",
          "Name",
          "Email",
          "Phone",
          "Consultation Type",
          "Service",
          "Status",
          "Actions",
        ];

      default:
        return [];
    }
  };

  const columns = getColumns();

  // 🔹 Export to Excel
  const exportToExcel = (tab) => {
    let dataSource = [];
    let columns = [];
    let sheetName = "";
    let fileName = "";

    switch (tab) {
      // --- Appointments ---
      case "appointments":
        dataSource = filteredAppointments;
        columns = [
          "Booking Id",
          "Booked On",
          "Scheduled Date",
          "Time",
          "Patient",
          "Consultation Type",
          "Email",
          "Phone",
          "Service",
          "Branch",
          "Pincode",
          "Locality",
          "FlatNo",
          "Landmark",
          "Status",
        ];
        sheetName = "Appointments";
        fileName = "appointments";
        break;

      // --- Quick Consultations ---
      case "quickConsultations":
        dataSource = filteredQuickConsultations;
        columns = ["Created On", "Name", "Phone", "Status", "User Response"];
        sheetName = "Quick Consultations";
        fileName = "quick_consultations";
        break;

      // --- Callback Requests ---
      case "callbackRequests":
        dataSource = filteredCallbackRequests;
        columns = [
          "Created On",
          "Name",
          "Phone",
          "Email",
          "Selected Service",
          "Status",
          "User Response",
        ];
        sheetName = "Callback Requests";
        fileName = "callback_requests";
        break;

      default:
        return;
    }

    if (!dataSource.length) {
      alert(`No ${sheetName} to export!`);
      return;
    }

    // Map the data to Excel rows
    const data = dataSource.map((a) => {
      const row = {};
      switch (tab) {
        case "appointments":
          row["Booking Id"] = a.booking_id || "-";
          row["Booked On"] = a.createdAt
            ? a.createdAt.toDate
              ? a.createdAt.toDate().toLocaleString()
              : new Date(a.createdAt).toLocaleString()
            : "-";
          row["Scheduled Date"] = a.date || "";
          row["Time"] = a.time || "";
          row["Patient"] = a.name || "-";
          row["Consultation Type"] = a.consultationType || "";
          row["Email"] = a.email || "";
          row["Phone"] = a.phone || "";
          row["Service"] = a.service || "";
          row["Branch"] = a.branch || "";
          row["Pincode"] = a.pincode || "";
          row["Locality"] = a.locality || "";
          row["FlatNo"] = a.flatNo || "";
          row["Landmark"] = a.landmark || "";
          row["Status"] = a.status || "";
          break;

        case "quickConsultations":
          row["Created On"] = a.createdAt
            ? a.createdAt.toDate
              ? a.createdAt.toDate().toLocaleString()
              : new Date(a.createdAt).toLocaleString()
            : "-";
          row["Name"] = a.name || "-";
          row["Phone"] = a.phone || "-";
          row["Status"] = a.status || "";
          row["User Response"] = a.userResponse || "";
          break;

        case "callbackRequests":
          row["Created On"] = a.createdAt
            ? a.createdAt.toDate
              ? a.createdAt.toDate().toLocaleString()
              : new Date(a.createdAt).toLocaleString()
            : "-";
          row["Name"] = a.name || "-";
          row["Phone"] = a.phone || "-";
          row["Email"] = a.email || "";
          row["Selected Service"] = a.selectedService || "";
          row["Status"] = a.status || "";
          row["User Response"] = a.userResponse || "";
          break;
      }
      return row;
    });

    // Generate Excel
    const worksheet = XLSX.utils.json_to_sheet(data, { header: columns });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(file, `${fileName}_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <div className="rounded-lg shadow-lg bg-white border border-gray-200 p-2 sm:p-4 md:p-6 overflow-x-auto">
      {/* Header & Filters */}
      <Tabs defaultValue="appointments" onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="quickConsultations">
              Quick Consultations
            </TabsTrigger>
            <TabsTrigger value="callbackRequests">
              Callback Requests
            </TabsTrigger>
          </TabsList>

          <Button
            onClick={() => exportToExcel(activeTab)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
          >
            Export to Excel
          </Button>
        </div>
        <TabsContent value="appointments">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
            <h2 className="text-md font-bold text-[#204080]">Appointments</h2>
            <div className="flex gap-3 flex-wrap">
              <input
                type="text"
                placeholder="Search by Booking ID"
                value={searchBookingId}
                onChange={(e) => setSearchBookingId(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="All">All Types</option>
                <option value="Walk-in">Walk-in</option>
                <option value="Home Visit">Home Visit</option>
                <option value="Virtual Consultation">
                  Virtual Consultation
                </option>
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="All">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Rescheduled">Rescheduled</option>
                <option value="Canceled">Canceled</option>
              </select>

              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gradient-to-br from-blue-50 to-slate-50">
                <tr>
                  {columns.map((h, i) => {
                    if (h === "Actions" && filterStatus === "Canceled") {
                      return null; // hide Actions col in header
                    }
                    return (
                      <th
                        key={i}
                        className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs sm:text-sm whitespace-nowrap"
                      >
                        {h}
                      </th>
                    );
                  })}
                  {filterStatus === "Canceled" && (
                    <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs sm:text-sm whitespace-nowrap">
                      Reason
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {filteredAppointments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-8 text-gray-500 italic"
                    >
                      No matching appointments
                    </td>
                  </tr>
                ) : (
                  filteredAppointments.map((appt, idx) => (
                    <tr
                      key={appt.id}
                      className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}
                    >
                      {/* Common fields */}
                      <td className="px-4 py-3">{appt.bookingId || "-"}</td>
                      <td className="px-4 py-3">
                        {appt.createdAt
                          ? appt.createdAt.toDate
                            ? appt.createdAt.toDate().toLocaleString()
                            : new Date(appt.createdAt).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-4 py-3">{appt.date}</td>
                      <td className="px-4 py-3">{appt.time}</td>
                      <td className="px-4 py-3">{appt.name || "-"}</td>
                      <td className="px-4 py-3">{appt.email || "-"}</td>
                      <td className="px-4 py-3">{appt.phone || "-"}</td>

                      {/* Walk-in */}
                      {filterType === "Walk-in" && (
                        <>
                          <td className="px-4 py-3">{appt.consultationType}</td>
                          <td className="px-4 py-3">{appt.service}</td>
                          <td className="px-4 py-3">{appt.branch || "-"}</td>
                          <td className="px-4 py-3">{appt.status}</td>
                        </>
                      )}

                      {/* Home Visit */}
                      {filterType === "Home Visit" && (
                        <>
                          <td className="px-4 py-3">{appt.consultationType}</td>
                          <td className="px-4 py-3">{appt.service}</td>
                          <td className="px-4 py-3">{appt.pincode || "-"}</td>
                          <td className="px-4 py-3">{appt.locality || "-"}</td>
                          <td className="px-4 py-3">{appt.flatNo || "-"}</td>
                          <td className="px-4 py-3">{appt.landmark || "-"}</td>
                          <td className="px-4 py-3">{appt.status}</td>
                        </>
                      )}

                      {/* Virtual */}
                      {filterType === "Virtual Consultation" && (
                        <>
                          <td className="px-4 py-3">{appt.consultationType}</td>
                          <td className="px-4 py-3">{appt.service}</td>
                          <td className="px-4 py-3">{appt.status}</td>
                        </>
                      )}

                      {/* All */}
                      {filterType === "All" && (
                        <>
                          <td className="px-4 py-3">{appt.consultationType}</td>
                          <td className="px-4 py-3">{appt.service}</td>
                          <td className="px-4 py-3">{appt.status}</td>
                        </>
                      )}

                      {/* Actions */}
                      {["Confirmed", "Canceled"].includes(
                        filterStatus
                      ) ? null : (
                        <td className="px-4 py-3 flex gap-2 flex-wrap">
                          {appt.status === "Pending" && (
                            <>
                              <button
                                onClick={() =>
                                  setPendingAction({
                                    id: appt.id,
                                    status: "Confirmed",
                                    appt,
                                  }) || setIsConfirmDialogOpen(true)
                                }
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              >
                                {loadingId === appt.id ? (
                                  <Loader2 className="animate-spin w-4 h-4" />
                                ) : (
                                  "Confirm"
                                )}
                              </button>

                              <button
                                onClick={() =>
                                  setPendingAction({
                                    id: appt.id,
                                    status: "Canceled",
                                    appt,
                                  }) || setIsConfirmDialogOpen(true)
                                }
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                              >
                                {loadingId === appt.id ? (
                                  <Loader2 className="animate-spin w-4 h-4" />
                                ) : (
                                  "Cancel"
                                )}
                              </button>

                              <button
                                onClick={() =>
                                  setPendingAction({
                                    id: appt.id,
                                    status: "Rescheduled",
                                    appt,
                                  }) || setIsConfirmDialogOpen(true)
                                }
                                className="px-3 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600"
                              >
                                {loadingId === appt.id ? (
                                  <Loader2 className="animate-spin w-4 h-4" />
                                ) : (
                                  "Reschedule"
                                )}
                              </button>
                            </>
                          )}
                          {appt.status === "Rescheduled" && (
                            <>
                              <button
                                onClick={() =>
                                  setPendingAction({
                                    id: appt.id,
                                    status: "Confirmed",
                                    appt,
                                  }) || setIsConfirmDialogOpen(true)
                                }
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              >
                                Confirm
                              </button>

                              <button
                                onClick={() =>
                                  setPendingAction({
                                    id: appt.id,
                                    status: "Canceled",
                                    appt,
                                  }) || setIsConfirmDialogOpen(true)
                                }
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </td>
                      )}
                      {filterStatus === "Canceled" && (
                        <td className="px-4 py-3">
                          {appt.cancelReason || "—"}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Confirmation Dialog */}
          {/* Confirmation Dialog */}
          <Dialog
            open={isConfirmDialogOpen}
            onOpenChange={setIsConfirmDialogOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-[#204080]">
                  {pendingAction?.status === "Canceled" && "Cancel Appointment"}
                  {pendingAction?.status === "Rescheduled" &&
                    "Reschedule Appointment"}
                  {pendingAction?.status === "Confirmed" &&
                    "Confirm Appointment"}
                </DialogTitle>
                <DialogDescription>
                  {pendingAction?.status === "Canceled" &&
                    "Are you sure you want to cancel this appointment?"}
                  {pendingAction?.status === "Rescheduled" &&
                    "Choose new date & time for rescheduling."}
                  {pendingAction?.status === "Confirmed" &&
                    "Are you sure you want to confirm this appointment?"}
                </DialogDescription>
              </DialogHeader>

              {/* Cancel → reason box */}
              {pendingAction?.status === "Canceled" && (
                <div className="flex flex-col gap-2 mt-3">
                  <label className="text-sm font-medium">
                    Reason for cancellation{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => {
                      setCancelReason(e.target.value);
                      if (e.target.value.trim()) setCancelError("");
                    }}
                    placeholder="Enter reason"
                    className="w-full p-2 border rounded-lg"
                    rows={3}
                  />
                  {cancelError && (
                    <p className="text-sm text-red-600">{cancelError}</p>
                  )}
                </div>
              )}

              {/* Reschedule → date + time */}
              {pendingAction?.status === "Rescheduled" && (
                <div className="flex flex-col gap-3 mt-3">
                  <div>
                    {/* <label className="text-sm font-medium">New Date</label> */}
                    <DatePicker
                      selected={
                        rescheduleDate ? new Date(rescheduleDate) : null
                      }
                      onChange={(date) => {
                        setRescheduleDate(
                          date ? format(date, "yyyy-MM-dd") : ""
                        );
                        setRescheduleError("");
                      }}
                      dateFormat="dd/MM/yyyy"
                      minDate={new Date()} // block past dates
                      placeholderText="Select a Date"
                      wrapperClassName="w-full"
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
                    />
                  </div>

                  <div>
                    {/* <label className="text-sm font-medium">New Time</label> */}
                    <select
                      value={rescheduleTime}
                      onChange={(e) => {
                        setRescheduleTime(e.target.value);
                        setRescheduleError("");
                      }}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
                    >
                      <option value="">Select time</option>
                      {[
                        "09:30 AM",
                        "10:00 AM",
                        "10:30 AM",
                        "11:00 AM",
                        "11:30 AM",
                        "12:00 PM",
                        "12:30 PM",
                        "01:00 PM",
                        "04:30 PM",
                        "05:00 PM",
                        "05:30 PM",
                        "06:00 PM",
                        "06:30 PM",
                        "07:00 PM",
                        "07:30 PM",
                        "08:00 PM",
                        "08:30 PM",
                        "09:00 PM",
                      ].map((slot) => (
                        <option key={slot} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </select>
                  </div>

                  {rescheduleError && (
                    <p className="text-sm text-red-600">{rescheduleError}</p>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsConfirmDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className={`
                  ${
                    buttonColorMap[pendingAction?.status] ||
                    "bg-[#204080] hover:bg-[#2E5AAC] text-white"
                  }
                  `}
                  onClick={async () => {
                    if (pendingAction) {
                      setLoadingId(pendingAction.id); // show loader
                      await handleUpdateStatus(
                        pendingAction.appt,
                        pendingAction.status
                      );
                      setLoadingId(null); // hide loader
                    }
                    setIsConfirmDialogOpen(false);
                  }}
                >
                  {loadingId === pendingAction?.id ? (
                    <Loader2 className="animate-spin w-4 h-4" />
                  ) : (
                    `Yes, ${statusLabelMap[pendingAction?.status]}`
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        {/* 🟡 Quick Consultations Tab */}
        <TabsContent value="quickConsultations">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-md font-bold text-[#204080]">
              Quick Consultations
            </h2>
            <input
              type="date"
              value={quickDate}
              onChange={(e) => setQuickDate(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gradient-to-br from-blue-50 to-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Service
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Booked On
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Actions
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    User Response
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredQuickConsultations.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-6 text-gray-500 italic"
                    >
                      No Quick Consultations
                    </td>
                  </tr>
                ) : (
                  filteredQuickConsultations.map((q) => (
                    <tr key={q.id} className="border-b">
                      <td className="px-4 py-3">{q.name}</td>
                      <td className="px-4 py-3">{q.phone}</td>
                      <td className="px-4 py-3">{q.selectedService}</td>
                      <td className="px-4 py-3">
                        {q.createdAt?.toDate
                          ? q.createdAt.toDate().toLocaleString()
                          : new Date(q.createdAt).toLocaleString()}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">{q.status || "Pending"}</td>

                      {/* Actions */}
                      <td className="px-4 py-3 flex gap-2">
                        <button
                          onClick={() => handleQuickStatus(q, "Answered")}
                          disabled={loadingId === q.id}
                          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {loadingId === q.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Answered"
                          )}
                        </button>

                        <button
                          onClick={() => handleQuickStatus(q, "No Answer")}
                          disabled={loadingId === q.id}
                          className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {loadingId === q.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "No Answer"
                          )}
                        </button>
                      </td>

                      {/* User Response */}
                      <UserResponseCell
                        id={q.id}
                        collection="quickConsultations"
                        initialValue={q.userResponse}
                      />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* 🔵 Callback Requests Tab */}
        <TabsContent value="callbackRequests">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-md font-bold text-[#204080]">
              Callback Requests
            </h2>
            <input
              type="date"
              value={callbackDate}
              onChange={(e) => setCallbackDate(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gradient-to-br from-blue-50 to-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Booked On
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    Actions
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[#204080] uppercase text-xs">
                    User Response
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCallbackRequests.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-6 text-gray-500 italic"
                    >
                      No Callback Requests
                    </td>
                  </tr>
                ) : (
                  filteredCallbackRequests.map((c) => (
                    <tr key={c.id} className="border-b">
                      <td className="px-4 py-3">{c.name}</td>
                      <td className="px-4 py-3">{c.phone}</td>
                      <td className="px-4 py-3">
                        {c.createdAt?.toDate
                          ? c.createdAt.toDate().toLocaleString()
                          : new Date(c.createdAt).toLocaleString()}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">{c.status || "Pending"}</td>

                      {/* Actions */}
                      <td className="px-4 py-3 flex gap-2">
                        <button
                          onClick={() => handleCallbackStatus(c, "Answered")}
                          disabled={loadingId === c.id}
                          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {loadingId === c.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Answered"
                          )}
                        </button>

                        <button
                          onClick={() => handleCallbackStatus(c, "No Answer")}
                          disabled={loadingId === c.id}
                          className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {loadingId === c.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "No Answer"
                          )}
                        </button>
                      </td>

                      {/* User Response */}
                      <UserResponseCell
                        id={c.id}
                        collection="callbackRequests"
                        initialValue={c.userResponse}
                      />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
